import { type LocalDate, localDate, lastDayOfMonth, parseLocalDate } from "./dates.js";

export type Frequency = "MONTHLY" | "QUARTERLY" | "ANNUAL" | "ONE_TIME" | "EVENT" | "ROLLING";

export type TaxPeriod =
  | { kind: "MONTH"; year: number; month: number }
  | { kind: "QUARTER"; year: number; quarter: number }
  | { kind: "YEAR"; year: number }
  | { kind: "DAY"; date: LocalDate };

export function monthPeriod(year: number, month: number): TaxPeriod {
  if (month < 1 || month > 12) throw new RangeError(`month ${month}`);
  return { kind: "MONTH", year, month };
}
export function quarterPeriod(year: number, quarter: number): TaxPeriod {
  if (quarter < 1 || quarter > 4) throw new RangeError(`quarter ${quarter}`);
  return { kind: "QUARTER", year, quarter };
}
export function yearPeriod(year: number): TaxPeriod {
  return { kind: "YEAR", year };
}

/** Stable key used in the database: 2026-09, 2026-Q3, 2026, 2026-09-14. */
export function periodKey(p: TaxPeriod): string {
  switch (p.kind) {
    case "MONTH":
      return `${p.year}-${String(p.month).padStart(2, "0")}`;
    case "QUARTER":
      return `${p.year}-Q${p.quarter}`;
    case "YEAR":
      return `${p.year}`;
    case "DAY":
      return p.date;
  }
}

export function parsePeriodKey(key: string): TaxPeriod {
  let m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) return monthPeriod(Number(m[1]), Number(m[2]));
  m = /^(\d{4})-Q([1-4])$/.exec(key);
  if (m) return quarterPeriod(Number(m[1]), Number(m[2]));
  m = /^(\d{4})$/.exec(key);
  if (m) return yearPeriod(Number(m[1]));
  parseLocalDate(key);
  return { kind: "DAY", date: key };
}

export function periodStart(p: TaxPeriod): LocalDate {
  switch (p.kind) {
    case "MONTH":
      return localDate(p.year, p.month, 1);
    case "QUARTER":
      return localDate(p.year, (p.quarter - 1) * 3 + 1, 1);
    case "YEAR":
      return localDate(p.year, 1, 1);
    case "DAY":
      return p.date;
  }
}

export function periodEnd(p: TaxPeriod): LocalDate {
  switch (p.kind) {
    case "MONTH":
      return lastDayOfMonth(p.year, p.month);
    case "QUARTER":
      return lastDayOfMonth(p.year, p.quarter * 3);
    case "YEAR":
      return localDate(p.year, 12, 31);
    case "DAY":
      return p.date;
  }
}

export function nextPeriod(p: TaxPeriod): TaxPeriod {
  switch (p.kind) {
    case "MONTH":
      return p.month === 12 ? monthPeriod(p.year + 1, 1) : monthPeriod(p.year, p.month + 1);
    case "QUARTER":
      return p.quarter === 4 ? quarterPeriod(p.year + 1, 1) : quarterPeriod(p.year, p.quarter + 1);
    case "YEAR":
      return yearPeriod(p.year + 1);
    case "DAY":
      throw new TypeError("DAY periods have no successor");
  }
}

/** The period of the given frequency that contains a date. */
export function periodContaining(freq: "MONTHLY" | "QUARTERLY" | "ANNUAL", date: LocalDate): TaxPeriod {
  const { y, m } = parseLocalDate(date);
  if (freq === "MONTHLY") return monthPeriod(y, m);
  if (freq === "QUARTERLY") return quarterPeriod(y, Math.floor((m - 1) / 3) + 1);
  return yearPeriod(y);
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** Spanish label: "septiembre 2026", "T3 2026", "2026". */
export function periodLabel(p: TaxPeriod): string {
  switch (p.kind) {
    case "MONTH":
      return `${MESES[p.month - 1]} ${p.year}`;
    case "QUARTER":
      return `T${p.quarter} ${p.year}`;
    case "YEAR":
      return `${p.year}`;
    case "DAY":
      return p.date;
  }
}
